import * as Sentry from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/core'
import { useEffect } from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import { settings } from './settings'

const { APP_ENV, SENTRY_DSN } = settings

/**
 * Erros de domínio que representam comportamento esperado da aplicação.
 * Estes erros são filtrados no beforeSend e não são enviados ao Sentry.
 */
export const IGNORED_ERROR_NAMES = [
  'BadRequestError',
  'ConflictError',
  'ForbiddenError',
  'InvalidCredentialsError',
  'NotFoundError',
  'TrackingError',
  'UnexpectedError'
]

/**
 * Nome do erro de transporte sem resposta HTTP (ver infra/http/network-error).
 * String literal de propósito: evita acoplar `main/config` à camada `infra`.
 */
const NETWORK_ERROR_NAME = 'NetworkError'

/**
 * Mensagens de erro genéricas/externas que devem ser ignoradas.
 */
const IGNORED_ERROR_MESSAGES = [
  /O conteúdo que você está tentando acessar não está disponível/i,
  'Network Error',
  'Importing a module script failed',
  'Load failed',
  // Rejeição emitida por extensões de browser (ex.: add-ons Office/Outlook) via
  // postMessage RPC — não é código da aplicação. Filtra o ruído na origem.
  /Object Not Found Matching Id/i
]

/**
 * Endpoints cujo erro 500 é degradado graciosamente pela aplicação (feature
 * secundária com fallback silencioso) — não geram impacto de UX e não devem
 * poluir o dashboard. A observabilidade desses 500s fica do lado do backend.
 */
const SILENCED_SERVER_ERROR_URLS = [/\/fit-score/i]

const isSilencedServerError = (event: ErrorEvent, hint: EventHint): boolean => {
  // Cobre tanto o ServerError de domínio (statusCode) quanto o padrão do Axios
  // (status / response.status), caso o caminho de captura mude.
  const error = hint.originalException as
    | { name?: string; statusCode?: number; status?: number; response?: { status?: number } }
    | undefined
  const isServerError =
    error?.name === 'ServerError' ||
    error?.statusCode === 500 ||
    error?.status === 500 ||
    error?.response?.status === 500
  if (!isServerError) return false

  // A URL pode estar no contexto http (setContext do errorMonitor) ou em request.url.
  const url =
    (event.contexts?.http?.url as string | undefined) ?? (event.request?.url as string) ?? ''
  return SILENCED_SERVER_ERROR_URLS.some(pattern => pattern.test(url))
}

export const isIgnoredError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return IGNORED_ERROR_NAMES.includes(error.name)
  }

  if (typeof error === 'object' && error !== null && 'name' in error) {
    return IGNORED_ERROR_NAMES.includes((error as { name: string }).name)
  }

  return false
}

const handleGenerateVersion = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')

  return `${APP_ENV}-v-${month}/${day}/${year}-${hours}:${minutes}:${seconds}`
}

export default {
  dsn: SENTRY_DSN,
  environment: APP_ENV,
  release: handleGenerateVersion(),
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration()
  ],
  ignoreErrors: IGNORED_ERROR_MESSAGES,
  denyUrls: [/user-guiding.*\.js/i, /userguiding/i],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.1,
  beforeSend(event: ErrorEvent, hint: EventHint) {
    if (isIgnoredError(hint.originalException)) {
      return null
    }

    if (isSilencedServerError(event, hint)) {
      return null
    }

    // NetworkError = request sem resposta HTTP (rede/CORS/proxy corporativo).
    // É mantido observável de propósito (network_verdict/correlationId/requestUrl
    // ajudam na análise de tickets), mas sempre como `info`: falha de rede do
    // cliente não é acionável pela engenharia e não deve paginar. Outage real da
    // nossa API chega como 5xx (level error) e no monitoramento de infra, não
    // aqui. Único ponto que cobre os dois caminhos de captura: o enriquecido
    // (React Query, que adiciona `network_verdict`) e o automático
    // (route-loaders/background, sem veredito).
    if ((hint.originalException as Error)?.name === NETWORK_ERROR_NAME) {
      // Dispositivo sem internet: nenhum valor de observabilidade — descarta.
      // (Para enviar também como `info`, troque por `event.level = 'info'`.)
      if (event.tags?.network_verdict === 'offline') {
        return null
      }

      event.level = 'info'
      event.tags = { ...event.tags, network_error: 'true' }
    }

    event.tags = {
      ...event.tags,
      route: window.location.pathname
    }

    event.contexts = {
      ...event.contexts,
      app: {
        url: window.location.href,
        referrer: document.referrer || undefined
      }
    }

    return event
  }
}
